import { AUTOMATIC_PRESENTATION_PRIORITIES, type AutomaticPresentationPriority } from './constants';
import {
    type AutomaticPresentationConditions,
    type AutomaticPresentationDebugMetadata,
    type AutomaticPresentationOptions,
    type AutomaticPresentationScope,
    type CreateAutomaticPresentationCoordinatorOptions,
} from './types';

const DEFAULT_ORDER = 0;
const DEFAULT_SCOPE: AutomaticPresentationScope = 'route';
const DEFAULT_IS_READY = true;

const priorityValues: readonly number[] = Object.values(AUTOMATIC_PRESENTATION_PRIORITIES);

/**
 * Внутреннее представление регистрации: значения по умолчанию уже раскрыты,
 * поэтому все необходимые для выбора поля имеют явные значения.
 */
export type NormalizedAutomaticPresentationOptions = Readonly<{
    id: string;
    priority: AutomaticPresentationPriority;
    eligibility: AutomaticPresentationOptions['eligibility'];
    order: number;
    scope: AutomaticPresentationScope;
    isReady: boolean;
    debug?: AutomaticPresentationDebugMetadata;
}>;

type RankedAutomaticPresentationRecord = Readonly<{
    token: number;
    options: NormalizedAutomaticPresentationOptions;
}>;

type NormalizedAutomaticPresentationConditions = Readonly<{
    eligibility: AutomaticPresentationConditions['eligibility'];
    isReady: boolean;
}>;

// Защищает таймер ожидания от некорректного значения,
// которое может прийти в JavaScript во время выполнения.
export function assertAutomaticPresentationCoordinatorOptions(
    options: CreateAutomaticPresentationCoordinatorOptions
): void {
    if (!Number.isFinite(options.waitingCandidateTimeoutMs) || options.waitingCandidateTimeoutMs < 0) {
        throw new Error('Таймаут ожидания кандидата должен быть конечным неотрицательным числом');
    }
}

// Проверяет данные, влияющие на идентификацию и сортировку:
// одной типизации недостаточно для значений, приходящих во время выполнения.
function assertAutomaticPresentationOptions(options: AutomaticPresentationOptions): void {
    if (options.id.trim().length === 0) {
        throw new Error('Идентификатор автоматического показа не должен быть пустым');
    }

    if (!priorityValues.includes(options.priority)) {
        throw new Error(`Неизвестный приоритет автоматического показа: ${options.priority}`);
    }

    if (options.order !== undefined && !Number.isFinite(options.order)) {
        throw new Error('Порядок автоматического показа должен быть конечным числом');
    }
}

// Раскрывает значения по умолчанию при регистрации,
// чтобы последующие `update` меняли только текущие условия показа.
export function normalizeAutomaticPresentationOptions(
    options: AutomaticPresentationOptions
): NormalizedAutomaticPresentationOptions {
    assertAutomaticPresentationOptions(options);

    const conditions = normalizeAutomaticPresentationConditions(options);

    return {
        id: options.id,
        priority: options.priority,
        eligibility: conditions.eligibility,
        order: options.order ?? DEFAULT_ORDER,
        scope: options.scope ?? DEFAULT_SCOPE,
        isReady: conditions.isReady,
        ...(options.debug ? { debug: { ...options.debug } } : {}),
    };
}

// `update` передаёт полный снимок условий:
// отсутствие `isReady` каждый раз означает готовность, а не сохранение старого значения.
export function normalizeAutomaticPresentationConditions(
    conditions: AutomaticPresentationConditions
): NormalizedAutomaticPresentationConditions {
    return {
        eligibility: conditions.eligibility,
        isReady: conditions.isReady ?? DEFAULT_IS_READY,
    };
}

// Задаёт порядок, не зависящий от очередности монтирования:
// `priority` по убыванию, затем `order` и `id` по возрастанию.
// `token` нужен только для устойчивого порядка ошибочно продублированных регистраций с одинаковым `id`.
export function compareAutomaticPresentationRecords(
    left: RankedAutomaticPresentationRecord,
    right: RankedAutomaticPresentationRecord
): number {
    if (left.options.priority !== right.options.priority) {
        return right.options.priority - left.options.priority;
    }

    if (left.options.order !== right.options.order) {
        return left.options.order - right.options.order;
    }

    if (left.options.id !== right.options.id) {
        return left.options.id < right.options.id ? -1 : 1;
    }

    return left.token - right.token;
}

// Исключает все экземпляры повторяющегося `id`:
// координатор не угадывает, какой из владельцев должен получить слот.
export function getDuplicateAutomaticPresentationIds(
    records: readonly RankedAutomaticPresentationRecord[]
): Set<string> {
    const counts = new Map<string, number>();

    records.forEach(({ options }) => {
        counts.set(options.id, (counts.get(options.id) ?? 0) + 1);
    });

    const duplicateIds = new Set<string>();

    counts.forEach((count, id) => {
        if (count > 1) {
            duplicateIds.add(id);
        }
    });

    return duplicateIds;
}
